import React from "react";
import PropTypes from "prop-types";
import { Map } from "immutable";
import { Field } from "redux-form";
import { toOptions } from "LIB/forms";
import removeKey, { removeKeys } from "LIB/removeKey";
import { Col } from "react-bootstrap";
import {
  CurrencyField,
  FormInput,
  FormRow,
  MonthYearField,
  Select,
  AutoCompleteField
} from "MODULES/shared/components/Forms";
import {
  APPROVAL_STATUSES,
  AWAITING_TO_SHIP,
  REPAIR_AWAITING_EVALUATION,
  REPAIR_EVALUATION_APPROVED,
  REPAIR_EVALUATION_RECEIVED,
  REPAIR_EVALUATION_REJECTED,
  BENCH_TEST,
  INSPECTED,
  SHOP_ASSIST,
  WARRANTY_CONSIDERATION,
  REPAIR,
  REPAIR_AND_HOLD,
  REPAIRED,
  OVERHAUL,
  OVERHAULED,
  TEARDOWN,
  EXPECTED_COMPONENT,
  APPROVE_OR_DENY_OPTIONS,
  REASONS_FOR_RETURN,
  UNIT_RETURNED_TO_CUSTOMER,
  APPROVAL_STATUS,
  ORIGINAL_EVALUATION,
  ACTION_REQUESTED,
  NOT_TO_EXCEED,
  EVALUATION_CHARGE,
  INTERNAL_OUTRIGHT,
  EXPECTED_SERIAL_NUMBER,
  EXCHANGE,
  CORE_RETURN_FEE_SAVINGS,
  LABOR_COST,
  OUTSIDE_LABOR_COST,
  PART_COST,
  REPAIR_STATION_WARRANTY_STATUS,
  RECOVERED_REPAIR_FROM_INTERNAL_OUTRIGHT,
  EVAL_STATUS
} from "MODULES/RepairOrders/constants";
import translateProps from "MODULES/shared/decorators/translateProps";
import {
  calculateSubtotal,
  coreReturnFeeSavingsVal
} from "MODULES/RepairOrders/helpers";
import ConditionWarning from "MODULES/inventories/components/EditInventory/ConditionWarning";
import {
  CONDITION,
  INVENTORIES,
  INVENTORY,
  PART_ID,
  TAG_DATE,
  CAAC,
  EASA,
  FAA,
  OTHER,
  TCCA
} from "MODULES/inventories/constants";
import { properCase } from "LIB/formatters/text";
import { WARRANTY_STATUS } from "MODULES/RMAsAndWarranties/constants";
import { searchOpenRO, getLineItems } from "MODULES/RepairOrders/actions";
import { ID } from "APP_ROOT/constants";
import { FLAT_EXCHANGE } from "MODULES/SalesOrders/constants";
import ROLineItemsTable from "./ROLineItemsTable";
import ExchangeWarning from "./ExchangeWarning";
import InternalOutrightConsiderationWarning from "./InternalOutrightConsiderationWarning";

const getFieldName = (index = undefined, name) => {
  if (index !== undefined) {
    return `line_items[${index}].${name}`;
  }
  return name;
};

export default function ActionFormSection({
  action,
  change,
  exchange,
  setField,
  getField,
  getInitialFieldValue,
  editing,
  disabled = false,
  warehouseDirector = false,
  showEvaluationFields = true,
  index,
  originalEvalGreaterThanOutrightVal,
  outrightValue,
  shopAssistRepairOrder,
  nested,
  repairOrder,
  tierType
}) {
  const evalFields = showEvaluationFields && (
    <EvaluationFields
      change={change}
      exchange={exchange}
      getField={getField}
      getInitialFieldValue={getInitialFieldValue}
      disabled={disabled}
      warehouseDirector={warehouseDirector}
      index={index}
      outrightValue={outrightValue}
      originalEvalGreaterThanOutrightVal={originalEvalGreaterThanOutrightVal}
      setField={setField}
      tierType={tierType}
      showExceedField={[REPAIR, OVERHAUL, BENCH_TEST].includes(action)}
    />
  );

  if (
    [BENCH_TEST, EXPECTED_COMPONENT, OVERHAUL, REPAIR, TEARDOWN].includes(
      action
    )
  ) {
    return editing || getField(ACTION_REQUESTED) === EXPECTED_COMPONENT ? (
      evalFields
    ) : (
      <FormRow key="original-eval-field">
        <Col md={3}>
          <CurrencyField
            name={getFieldName(index, NOT_TO_EXCEED)}
            label={`${properCase(NOT_TO_EXCEED)}`}
            disabled={disabled || getField(getFieldName(index, FLAT_EXCHANGE))}
          />
        </Col>
      </FormRow>
    );
  }

  return {
    [WARRANTY_CONSIDERATION]: (
      <div>
        {evalFields}
        <WarrantyFields
          getField={getField}
          index={index}
          editing={editing}
          disabled={disabled || warehouseDirector}
        />
      </div>
    ),
    [SHOP_ASSIST]: (
      <div>
        <ShopAssistCost
          setField={setField}
          getField={getField}
          index={index}
          shopAssistRepairOrder={shopAssistRepairOrder}
          nested={nested}
          repairOrder={repairOrder}
          disabled={disabled || warehouseDirector}
        />
      </div>
    ),
    [BENCH_TEST]: <span />
  }[action];
}

ActionFormSection.propTypes = {
  action: PropTypes.string,
  exchange: PropTypes.bool,
  setField: PropTypes.func,
  getField: PropTypes.func,
  getInitialFieldValue: PropTypes.func,
  shopAssistRepairOrder: PropTypes.shape({}),
  nested: PropTypes.bool,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  warehouseDirector: PropTypes.bool,
  originalEvalGreaterThanOutrightVal: PropTypes.bool,
  outrightValue: PropTypes.number,
  showEvaluationFields: PropTypes.bool,
  index: PropTypes.number,
  repairOrder: PropTypes.instanceOf(Map)
};

const withApprovalStatus = translateProps(({ getField }) => ({
  approvalStatus: getField(APPROVAL_STATUS)
}));

const evaluationFeeField = ({ disabled, approvalStatus }) =>
  approvalStatus === REPAIR_EVALUATION_REJECTED && (
    <CurrencyField
      name={EVALUATION_CHARGE}
      label="Evaluation Fee"
      disabled={disabled}
      required
    />
  );

const EvaluationFeeField = withApprovalStatus(evaluationFeeField);

export const approvalStatuses = (
  approvalStatus,
  originalEval,
  actionRequested
) => {
  if (approvalStatus === UNIT_RETURNED_TO_CUSTOMER) {
    return APPROVAL_STATUSES;
  } else if (approvalStatus === AWAITING_TO_SHIP) {
    return removeKey(APPROVAL_STATUSES, UNIT_RETURNED_TO_CUSTOMER);
  } else if (
    [EXPECTED_COMPONENT, UNIT_RETURNED_TO_CUSTOMER].includes(actionRequested) ||
    approvalStatus === UNIT_RETURNED_TO_CUSTOMER
  ) {
    return removeKeys(APPROVAL_STATUSES, [
      AWAITING_TO_SHIP,
      UNIT_RETURNED_TO_CUSTOMER
    ]);
  } else if (originalEval) {
    return removeKeys(APPROVAL_STATUSES, [
      AWAITING_TO_SHIP,
      UNIT_RETURNED_TO_CUSTOMER
    ]);
  }
  return removeKeys(APPROVAL_STATUSES, [
    AWAITING_TO_SHIP,
    UNIT_RETURNED_TO_CUSTOMER
  ]);
};

function EvaluationFields({
  change,
  exchange,
  getField,
  getInitialFieldValue,
  disabled,
  warehouseDirector,
  index,
  outrightValue,
  setField,
  tierType,
  originalEvalGreaterThanOutrightVal,
  showExceedField
}) {
  const handleApprovalStatusChange = (event, newValue) => {
    if (newValue === REPAIR_EVALUATION_REJECTED) {
      change(APPROVAL_STATUS, REPAIR_EVALUATION_REJECTED);
      [FAA, EASA, CAAC, TCCA, OTHER].forEach(field => {
        change(field, false);
      });
    }
  };

  const filteredOptions = approvalStatuses(
    getInitialFieldValue(APPROVAL_STATUS),
    getField(ORIGINAL_EVALUATION),
    getField(ACTION_REQUESTED)
  );

  const tagDateRequiredConditions = [OVERHAULED, INSPECTED, REPAIRED];
  const hasFlatExchange = getField(
    "inventory.most_recent_sales_order.flat_exchange"
  );

  const changeOriginalEval = approvalStatus => {
    if (approvalStatus === REPAIR_AWAITING_EVALUATION) {
      change(ORIGINAL_EVALUATION, "0.00");
    }
  };
  const subtotal = calculateSubtotal(getField);

  return (
    <div>
      <FormRow>
        <Col md={3}>
          <CurrencyField
            name={ORIGINAL_EVALUATION}
            label={properCase(ORIGINAL_EVALUATION)}
            required={[
              REPAIR_EVALUATION_APPROVED,
              REPAIR_EVALUATION_RECEIVED
            ].includes(getField(APPROVAL_STATUS))}
            disabled={disabled || warehouseDirector}
            onChange={(_e, val) => {
              if (exchange && !getField(FLAT_EXCHANGE)) {
                setField(
                  CORE_RETURN_FEE_SAVINGS,
                  coreReturnFeeSavingsVal(
                    val,
                    subtotal([LABOR_COST, OUTSIDE_LABOR_COST, PART_COST], 0)
                  )
                );
              }
            }}
          />
        </Col>
        <Col md={3}>
          <Field
            name={APPROVAL_STATUS}
            component={Select}
            options={toOptions(filteredOptions)}
            label={properCase(EVAL_STATUS)}
            required
            disabled={
              disabled ||
              warehouseDirector ||
              (getField(INTERNAL_OUTRIGHT) &&
                !getField(RECOVERED_REPAIR_FROM_INTERNAL_OUTRIGHT)) ||
              getInitialFieldValue(APPROVAL_STATUS) === AWAITING_TO_SHIP
            }
            onChange={(ev, val) => {
              changeOriginalEval(val);
              handleApprovalStatusChange(ev, val);
            }}
          />
        </Col>
        {showExceedField && (
          <Col md={3}>
            <CurrencyField
              name={getFieldName(index, NOT_TO_EXCEED)}
              label={properCase(NOT_TO_EXCEED)}
              disabled={disabled || warehouseDirector || hasFlatExchange}
            />
          </Col>
        )}
        <Col md={3}>
          <EvaluationFeeField
            getField={getField}
            disabled={warehouseDirector}
          />
        </Col>
        {originalEvalGreaterThanOutrightVal &&
          getField("inventory.exchange_eval_in_progress") &&
          getField(APPROVAL_STATUS) === REPAIR_EVALUATION_REJECTED && (
            <React.Fragment>
              <ExchangeWarning />
            </React.Fragment>
          )}
        {getField(EXCHANGE) && (
          <InternalOutrightConsiderationWarning
            actionRequested={getField(ACTION_REQUESTED)}
            condition={getField(`inventory.${CONDITION}`)}
            inventoryId={getField("inventory_id")}
            outrightValue={outrightValue}
            originalEval={getField(ORIGINAL_EVALUATION)}
            partId={getField(PART_ID)}
            tierType={tierType}
          />
        )}
      </FormRow>
      <FormRow>
        {getField(APPROVAL_STATUS) === REPAIR_EVALUATION_APPROVED && [
          <Col md={3}>
            <Field
              name={[INVENTORY, CONDITION].join(".")}
              label={properCase([INVENTORY, CONDITION].join("_"))}
              component={Select}
              options={toOptions(INVENTORIES.condition)}
              disabled={disabled || warehouseDirector}
              required
            />
          </Col>,
          <Col md={4}>
            <div className="form-group">
              <MonthYearField
                name={TAG_DATE}
                component={FormInput}
                label={properCase(TAG_DATE)}
                disabled={
                  disabled || warehouseDirector || getField(REPAIR_AND_HOLD)
                }
                required={
                  tagDateRequiredConditions.includes(
                    getField([INVENTORY, CONDITION].join("."))
                  ) && !getField(REPAIR_AND_HOLD)
                }
              />
            </div>
          </Col>
        ]}

        {getField(ACTION_REQUESTED) === EXPECTED_COMPONENT && (
          <Col md={3}>
            <Field
              name={EXPECTED_SERIAL_NUMBER}
              component={FormInput}
              label={properCase(EXPECTED_SERIAL_NUMBER)}
              required
            />
          </Col>
        )}
      </FormRow>

      <ConditionWarning
        inventoryCondition={getInitialFieldValue(CONDITION)}
        condition={getField(CONDITION)}
        canClose={false}
      />
    </div>
  );
}

EvaluationFields.propTypes = {
  change: PropTypes.func.isRequired,
  exchange: PropTypes.bool,
  getField: PropTypes.func,
  getInitialFieldValue: PropTypes.func,
  setField: PropTypes.func,
  disabled: PropTypes.bool,
  warehouseDirector: PropTypes.bool,
  showExceedField: PropTypes.bool,
  index: PropTypes.number,
  outrightValue: PropTypes.number,
  originalEvalGreaterThanOutrightVal: PropTypes.bool,
  tierType: PropTypes.string
};

class ShopAssistCost extends React.Component {
  state = {
    selectedRO: {},
    lineItems: []
  };

  componentDidMount() {
    if (this.props.shopAssistRepairOrder) {
      this.handleROSelected(this.props.shopAssistRepairOrder);
    }
  }

  handleROSelected = ro => {
    this.setState({ selectedRO: ro });
    if (ro && ro.id) {
      getLineItems(ro.id).then(resp => {
        this.setState({ lineItems: resp || [] });
      });
    }
  };

  render() {
    const { selectedRO, lineItems } = this.state;
    const {
      setField,
      getField,
      nested,
      index,
      disabled,
      repairOrder
    } = this.props;
    const baseName = "shop_assist_repair_order_id";

    return (
      <React.Fragment>
        <FormRow key="ro_search_field">
          {!nested && <h4>Higher Assembly</h4>}
          <Col md={6}>
            <Field
              name={getFieldName(index, baseName)}
              label="RO #"
              placeholder="Type a Repair Order's code..."
              component={AutoCompleteField}
              search={filter =>
                searchOpenRO(filter, repairOrder && repairOrder.get(ID))}
              selectedItem={selectedRO}
              formatItem={result => result && result.code}
              onItemSelected={i => i && this.handleROSelected(i)}
              disabled={disabled}
              required
            />
          </Col>
        </FormRow>
        <FormRow key="part_results">
          <Col md={12}>
            {lineItems.length > 0 && (
              <ROLineItemsTable
                results={lineItems}
                index={index}
                setField={setField}
                getField={getField}
                nested={nested}
                disabled={disabled}
              />
            )}
          </Col>
        </FormRow>
        {!nested && (
          <FormRow key="shop_assist_costs">
            <Col md={4}>
              <CurrencyField
                name={getFieldName(index, "original_piece_part_cost")}
                label="Original PP Cost From Eval"
                disabled
              />
            </Col>
            <Col md={3}>
              <CurrencyField
                name={getFieldName(index, "final_piece_part_cost")}
                label="Final PP Cost"
                disabled
              />
            </Col>
            <Col md={3}>
              <CurrencyField
                name={getFieldName(index, "core_return_cost_savings")}
                label="Core Return Cost Savings"
                disabled
              />
            </Col>
          </FormRow>
        )}
      </React.Fragment>
    );
  }
}

ShopAssistCost.propTypes = {
  shopAssistRepairOrder: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string
  }),
  getField: PropTypes.func.isRequired,
  setField: PropTypes.func.isRequired,
  nested: PropTypes.bool,
  disabled: PropTypes.bool,
  index: PropTypes.number,
  repairOrder: PropTypes.instanceOf(Map)
};

const warrantyRequired = (idx, getField) =>
  getField(getFieldName(idx, "customer_rma_id")) &&
  [REPAIR_EVALUATION_APPROVED, REPAIR_EVALUATION_REJECTED].includes(
    getField(getFieldName(idx, APPROVAL_STATUS))
  );

const WarrantyFields = ({ index, disabled = false, editing, getField }) => [
  <div className="row row-col-gutters-half" key="warranty_1">
    <Col md={6}>
      <Field
        name={getFieldName(index, "reason_for_return")}
        component={Select}
        options={REASONS_FOR_RETURN}
        label="Reason For Return"
        disabled={disabled}
      />
    </Col>
    {editing && (
      <Col md={3}>
        <Field
          name={getFieldName(index, WARRANTY_STATUS)}
          required={warrantyRequired(index, getField)}
          component={Select}
          options={toOptions(APPROVE_OR_DENY_OPTIONS)}
          label="Warranty"
          disabled={disabled}
        />
      </Col>
    )}
    {editing && (
      <Col md={3}>
        <Field
          name={getFieldName(index, REPAIR_STATION_WARRANTY_STATUS)}
          required={warrantyRequired(index, getField)}
          component={Select}
          options={toOptions(APPROVE_OR_DENY_OPTIONS)}
          label="Repair Station Warranty"
          disabled={disabled}
        />
      </Col>
    )}
  </div>
];



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Detail/LineItems/FormSections/ActionRequested/ActionFormSection.jsx